import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { defaultFixturesExportFieldNames, fixturesExportFields } from "~pages/Fixtures/FixtureListPage/constants/exportFields";
import { useExportFixtures } from "~pages/Fixtures/FixtureListPage/hooks/useExportFixtures.ts";
import { useImportFixtures } from "~pages/Fixtures/FixtureListPage/hooks/useImportFixtures.tsx";
import {
  BroadcastsFixtureAction,
  DeleteFixtureAction,
  LockUnlockFixtureAction,
  ModalEditFixtureAction,
  ResetFixtureAction,
  UpsertFixtureModal,
} from "./components";
import { useFetchAllSeasonFixtures, useFilterFixtures } from "./hooks";
import type { FixtureTransformed } from "./types";
import "./FixtureListPage.scss";
import { Divider } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { CompetitorTypes } from "~enums/competitorTypes.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import ShowRosterFixtureAction from "~pages/Fixtures/FixtureListPage/components/Actions/ShowRosterFixtureAction.tsx";
import { useGetFixtureTableColumns } from "~pages/Fixtures/FixtureListPage/hooks/useGetFixtureTableColumns.tsx";
import { useGetFixtureTableFilters } from "~pages/Fixtures/FixtureListPage/hooks/useGetFixtureTableFilters.tsx";

const FixtureListPage = () => {
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const sportConfiguration = useSportConfiguration();
  const { uiConfiguration } = sportConfiguration;
  const dataTableImportConfiguration = uiConfiguration.pages.fixtures.listPage.importConfig;
  const dataTableUiConfiguration = uiConfiguration.pages.fixtures.listPage;
  const { data: serverFixtures, isLoading } = useFetchAllSeasonFixtures();
  const { currentFilter, handleFilterChange, filters } = useGetFixtureTableFilters({
    competitorType: sportConfiguration.competitorType,
    serverFixtures: serverFixtures as any,
  });
  const clientFixtures: FixtureTransformed[] = useFilterFixtures(serverFixtures as any, currentFilter);
  const [massEditMode, setMassEditMode] = useState<boolean>(false);
  const [isNewFixtureModelOpened, setIsNewFixtureModelOpened] = useState<boolean>(false);
  const [modalEditFixture, setModalEditFixture] = useState<FixtureTransformed | null>(null);
  const { customerId, organizationId, seasonId, sport } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useUserPermissions();

  const onMassEditModeChangedHandler = useCallback(
    (checked: boolean) => {
      setMassEditMode(checked);
      return queryClient.invalidateQueries({ queryKey: ["fixtureListPage"] });
    },
    [queryClient],
  );

  const onNewFixtureButtonClickedHandler = useCallback(() => setIsNewFixtureModelOpened((prevState) => !prevState), []);
  const onRefreshButtonClickedHandler = useCallback(() => queryClient.invalidateQueries({ queryKey: ["fixtureListPage"] }), [queryClient]);

  const { mutateAsync: startImporting } = useImportFixtures();
  const { mutateAsync: startExport, isPending: isExportPending } = useExportFixtures();

  const columnChooserLocalStorageKey = `${sport}.tables.settings.columns.fixtures`;
  const sorterLocalStorageKey = `${sport}.tables.settings.sort.fixtures`;

  const columns = useGetFixtureTableColumns(massEditMode);
  const paginationConfig = useHandlePagination();

  const total = clientFixtures.length;

  const hasPermissionToUpdate = hasPermission(PermissionNames.FIXTURE, PermissionOperations.UPDATE);
  const hasPermissionToDelete = hasPermission(PermissionNames.FIXTURE, PermissionOperations.DELETE);
  const hasPermissionToUpdateFixtureSettings = hasPermission(PermissionNames.FIXTURE_SETTINGS, PermissionOperations.UPDATE);

  const getNavigateUrl = (fixtureId: string) => `/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/fixtures/${fixtureId}`;

  return (
    <div>
      <DataTable
        title={formatMessage({ id: "fixtureListPage.title" })}
        isDataLoading={isLoading}
        columns={columns}
        rowActions={{
          base: {
            manage: {
              onClick: (row) => navigate(getNavigateUrl(row.fixtureId)),
              intlKey: "fixtureListPage.table.column.actions.advancedEdit",
            },
          },
          custom: {
            edit: {
              renderAction: (row) => <ModalEditFixtureAction onModalEditModeClickedHandler={() => setModalEditFixture(row)} />,
              hidden: () => !hasPermissionToUpdate,
              disabled: (row) => row.locked,
            },
            broadcast: {
              renderAction: (row) => <BroadcastsFixtureAction fixtureId={row.fixtureId} />,
              hidden: () => !hasPermissionToUpdate,
            },
            divider1: {
              renderAction: () => <Divider style={{ margin: 0 }} />,
              hidden: () => !hasPermissionToUpdateFixtureSettings && !hasPermissionToDelete,
            },
            roster: {
              renderAction: (row) => <ShowRosterFixtureAction fixtureId={row.fixtureId} />,
              hidden: () => sportConfiguration.competitorType !== CompetitorTypes.Entity || !hasPermissionToUpdateFixtureSettings,
            },
            lockUnlock: {
              renderAction: (row) => <LockUnlockFixtureAction fixture={row} />,
              hidden: () => !hasPermissionToUpdateFixtureSettings,
            },
            reset: {
              renderAction: (row) => <ResetFixtureAction fixtureId={row.fixtureId} />,
              disabled: (row) => row.locked,
              hidden: () => !hasPermissionToUpdateFixtureSettings,
            },
            deleteFixture: {
              renderAction: (row) => <DeleteFixtureAction fixture={row} />,
              disabled: (row) => row.locked,
              hidden: () => !hasPermissionToDelete,
            },
          },
        }}
        permissionNames={[PermissionNames.FIXTURE]}
        addButtonIntlKey={"add.fixture"}
        columnChooserLocalStorageKey={columnChooserLocalStorageKey}
        rowKey={"fixtureId"}
        dataTableUiConfiguration={dataTableUiConfiguration}
        columnSortConfig={{
          sorterLocalStorageKey: sorterLocalStorageKey,
          defaultSorter: { columnKey: "fixtureId", order: "ascend" },
        }}
        dataSource={clientFixtures}
        handleAdd={onNewFixtureButtonClickedHandler}
        exportConfig={{
          handleExport: startExport,
          localStorageKey: "export.fixtures",
          loading: isExportPending,
          defaultFields: defaultFixturesExportFieldNames,
          groupedFields: fixturesExportFields,
        }}
        importConfig={{
          ...dataTableImportConfiguration,
          handleImport: startImporting,
        }}
        filtersConfig={{ filters, handleFilterChange, currentFilter }}
        handleRefresh={onRefreshButtonClickedHandler}
        onMassEditModeChangedHandler={onMassEditModeChangedHandler}
        massEditMode={massEditMode}
        paginationConfig={{ ...paginationConfig, total }}
      />
      {modalEditFixture && <UpsertFixtureModal fixture={modalEditFixture} onClose={() => setModalEditFixture(null)} />}
      {isNewFixtureModelOpened && <UpsertFixtureModal onClose={() => setIsNewFixtureModelOpened(false)} />}
    </div>
  );
};

export default FixtureListPage;
